import { FormikProps } from 'formik';
import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormField } from '../../../../../../global/components/form-element/form-field';
import { DividerSection } from '../../../../../../global/components/layout/divider-section';
import { buttonStyles } from '../../../../../../global/constants/theme/styles/components/buttonstyles';
import { DemographicsContext } from '../../../../../../global/models/demographics-model';
import { LabelSettingProps } from '../../../../../../global/models/epi-model';
import { Person } from '../../../../../../global/models/person-model';
import { TcsmStates } from '../../../../../../root/tcsm-model';
import { EmailsDisplay } from '../../../../../account-info/components/read-only/emails-display';
import { MailingAddressDisplay } from '../../../../../account-info/components/read-only/mailing-address-display';
import { MaritalStatusDisplay } from '../../../../../account-info/components/read-only/marital-status-display';
import { PersonFieldsContentDisplay } from '../../../../../account-info/components/read-only/person-fields-display';
import { PhonesDisplay } from '../../../../../account-info/components/read-only/phones-display';
import { AccountInfoRoutes } from '../../../../../account-info/constants/routes';
import { Spouse } from '../../../../../account-info/features/relationships/relationships-model';
import { getCurrentSpouse } from '../../../../../account-info/features/relationships/relationships-selectors';
import { getInitialEmails, getInitialMailingAddress, getInitialPersonInfo, getInitialPhones } from '../../../../../account-info/helpers/form-init-helpers';
import { AnnualEnrollmentCommentsDisplay } from '../../../../../annual-enrollment/components/read-only/annual-enrollment-comments-display';
import { AnnualEnrollmentProps, NewHireProps } from '../../../../models/arc-epi-props';
import { NewHireEnrollmentRoutes } from '../../models/new-hire-routes';
import { BenefitEnrollmentStep } from '../../models/pension-enrollment-models';
import { setCurrentNewHireBenefitEnrollmentStep } from '../../slice/new-hire-enrollment-slices';
import { NewHireCoverageState } from '../models/coverage-models';
import { ParticipantReview } from './coverage-participant-review';
import { CoveragePlansReview } from './coverage-plans-review';
import { CoverageReviewDependent } from './coverage-review-dependent';
import { CoverageEmploymentCompensationReview } from './employment-compensation-review';
import { HealthPlanReview } from './health-plan-review';
import { PensionReview } from './pension-review';
import { AnnualEnrollmentCoverageReviewDisplay } from './annual-enrollment-coverage-review-display';
import { getCurrentTimeNY, getNumberOfDays } from '../../../../../../global/helpers/date-helpers';
import { ShouldShowAdminCafeteriaAcknowledgement, validatePlanContentDioApprover } from '../helpers/coverage-form-helpers';
import { useEmploymentInfo } from '../../../employment/useEmployment';
import { ContentSpacer } from '../../../../../../global/components/layout/generic-content-spacer';
import { TcsmModal } from '../../../../../../global/components/tcsm-modal';


interface Props {
	settings: NewHireProps;
	labelSettings: LabelSettingProps;
	formProps: FormikProps<any>;
	setEditUrl: any;
	basePath: string;
	isParticipant?: boolean;
	isDioApprover?: boolean;
	qualifyingEventDateOverwrite?: string;
}

export function CoverageReview(props: Props) {

	const { settings, labelSettings, formProps, setEditUrl, basePath, isParticipant, isDioApprover, qualifyingEventDateOverwrite } = props;
	useEmploymentInfo();
	const app = useContext(DemographicsContext);
	const aeProps = useSelector<TcsmStates, AnnualEnrollmentProps>(state => state?.epiProps?.annualEnrollmentSettingsState?.epiContent);
	const currentPerson = useSelector<TcsmStates, Person>(state => state?.personalInfo[app]?.currentPerson);
	const spouses = useSelector<TcsmStates, Spouse[]>(state => state?.relationships[app].spouses);
	const coverageState = useSelector<TcsmStates, NewHireCoverageState>(state => state?.newHireEnrollmentState?.newHireCoverageState);
	const hireDate = useSelector<TcsmStates, string>(state => state?.employmentState?.currentEmployment?.hire_date);
	const isLifeEventCoverageForm = useSelector<TcsmStates, boolean>(state => state?.newHireEnrollmentState?.isLifeEventCoverageForm);

	const currentDate = getCurrentTimeNY();
	const formattedHireDate = new Date(qualifyingEventDateOverwrite || hireDate?.replace(/-/g, '/'));
	const numberOfDays = getNumberOfDays(currentDate, formattedHireDate);

	const buttonClasses = buttonStyles();
	const dispatch = useDispatch();
	const [open, setOpen] = React.useState(false);

	const handleOpen = () => {
		setOpen(true);
	};
	const handleClose = () => {
		setOpen(false);
	};

	const editButtonAction = (url: string, step?: BenefitEnrollmentStep) => {
		if (step === BenefitEnrollmentStep.COVERAGE) {
			dispatch(setCurrentNewHireBenefitEnrollmentStep(step));
		}
		setEditUrl(url);
	};

	const renderEditButton = (url: string, step?: BenefitEnrollmentStep) => {
		return (
			<button className={buttonClasses.linkButton} type='button' onClick={() => editButtonAction(url, step)}>{aeProps?.editLabel}</button>
		);
	};

	return (
		<div className='grid-main-content'>
			{isDioApprover &&
				<ContentSpacer>
					<p>{settings?.submittedByLabel}<button className={buttonClasses.linkButton} type='button' onClick={handleOpen}>{coverageState?.submitted_by?.name}</button></p>
				</ContentSpacer>}
			<DividerSection removeDivider sectionText={settings?.personalInfoReviewSectionTitle} secondarySectionNode={renderEditButton(`${basePath}${AccountInfoRoutes.PersonalInfoSegment}`)}>
				<PersonFieldsContentDisplay
					labelSetting={labelSettings}
					values={getInitialPersonInfo(currentPerson)}
					dobMasked
				/>
			</DividerSection>
			<MailingAddressDisplay
				labelSetting={labelSettings}
				values={getInitialMailingAddress(currentPerson)}
			/>
			<DividerSection removeDivider>
				<PhonesDisplay
					labelSetting={labelSettings}
					values={getInitialPhones(currentPerson)}
				/>
			</DividerSection>
			<DividerSection>
				<EmailsDisplay
					labelSetting={labelSettings}
					values={getInitialEmails(currentPerson)}
				/>
			</DividerSection>
			<MaritalStatusDisplay
				labelSetting={labelSettings}
				annualEnrollmentProps={aeProps}
				person={currentPerson}
				spouse={getCurrentSpouse(spouses)}
				editLink={renderEditButton(`${basePath}${AccountInfoRoutes.RelationshipsSegment}`)}
			/>
			<CoverageReviewDependent
				dependents={coverageState?.eligible_dependents}
				settings={settings}
				editLink={renderEditButton(`${basePath}${AccountInfoRoutes.RelationshipsSegment}`)}
			/>
			{
				!isParticipant && <CoverageEmploymentCompensationReview settings={settings} />
			}
			{
				(!isParticipant && !isLifeEventCoverageForm) && <PensionReview settings={settings} editLink={!isDioApprover && renderEditButton(`${basePath}${NewHireEnrollmentRoutes.Pension}`)} />
			}
			<HealthPlanReview
				settings={settings}
				formValues={formProps?.values}
				coverageState={coverageState}
				labelSettings={labelSettings}
				isParticipant={isParticipant}
				isDioApprover={isDioApprover}
			/>
			<ParticipantReview
				settingsNewHire={settings}
				settings={aeProps}
				formValues={formProps?.values}
				dependents={coverageState?.eligible_dependents}
				editLink={renderEditButton(null, BenefitEnrollmentStep.COVERAGE)}
				hasMedical={coverageState?.eligible_plans?.medical?.plans?.length > 0}
				hasDental={coverageState?.eligible_plans?.dental?.plans?.length > 0}
				medicalPlansDisplayedDioApprover={validatePlanContentDioApprover(coverageState?.eligible_plans?.medical, isDioApprover)}
				dentalPlansDisplayedDioApprover={validatePlanContentDioApprover(coverageState?.eligible_plans?.dental, isDioApprover)}
				currentMedicalSelections={coverageState?.eligible_plans?.medical?.current}
				currentDentalSelections={coverageState?.eligible_plans?.dental?.current}
			/>
			<CoveragePlansReview
				settings={settings}
				formValues={formProps?.values}
				coverageState={coverageState}
				editLink={renderEditButton(null, BenefitEnrollmentStep.COVERAGE)}
				isdioApprover={isDioApprover}
			/>
			<div className='grid-main-content'>
				<DividerSection removeDivider>
					<AnnualEnrollmentCoverageReviewDisplay
						formikProps={formProps}
						plans={coverageState}
						settings={settings}
						isParticipant={isParticipant}
						dioApproverMedicalPlansDisplayed={validatePlanContentDioApprover(coverageState?.eligible_plans?.medical, isDioApprover)}
						dioApproverDentalPlansDisplayed={validatePlanContentDioApprover(coverageState?.eligible_plans?.dental, isDioApprover)}
						dioApproverShortTermPlansDisplayed={validatePlanContentDioApprover(coverageState?.eligible_plans?.short_term_disability, isDioApprover)}
						dioApproverLongTermPlansDisplayed={validatePlanContentDioApprover(coverageState?.eligible_plans?.long_term_disability, isDioApprover)}
						dioApproverUeiPlansDisplayed={validatePlanContentDioApprover(coverageState?.eligible_plans?.uei, isDioApprover)}
					/>
				</DividerSection>
			</div>
			<AnnualEnrollmentCommentsDisplay
				annualEnrollmentProps={aeProps}
				myNotes={formProps?.values?.memo}
				specialRequests={formProps?.values?.specialRequests}
				editButton={renderEditButton(null, BenefitEnrollmentStep.COVERAGE)}
				newHireProps={settings}
			/>
			<ContentSpacer>
				<FormField
					name='agreement'
					label={isParticipant ? settings.participantCoverageReviewCheckBoxText : settings?.coverageReviewCheckBoxText}
					component='checkBox'
					touched={formProps.touched}
					errors={formProps.errors}
					value={formProps?.values?.agreement ?? false}
					checked={formProps?.values?.agreement ?? false}
				/>
				{
					ShouldShowAdminCafeteriaAcknowledgement(coverageState, formProps?.values, numberOfDays) &&
					<FormField
						name='cafeteriaAcknowledgement'
						label={settings?.adminCafeteriaAcknowledgement}
						component='checkBox'
						touched={formProps.touched}
						errors={formProps.errors}
						value={formProps?.values?.cafeteriaAcknowledgement ?? false}
						checked={formProps?.values?.cafeteriaAcknowledgement ?? false}
					/>
				}
			</ContentSpacer>
			<TcsmModal
				open={open}
				handleClose={handleClose}
			><div className='rich-text'>
					<h3>{settings?.submitterContactInfoLabel}</h3>
					<h4>{coverageState?.submitted_by?.name}</h4>
					<p><strong>{settings?.submitterBusinessEmailLabel}</strong>: {coverageState?.submitted_by?.business_email}<br />
						<strong>{settings?.submitterBusinessPhoneLabel}</strong>: {coverageState?.submitted_by?.business_phone}</p>
				</div>
			</TcsmModal>
		</div>
	);
}

CoverageReview.defaultProps = {
	isParticipant: false,
	isDioApprover: false,
	qualifyingEventDateOverwrite: null,
};